import { api } from "./api";

const SID_KEY = "analytics_sid";

// --- Attribuzione UTM (consent-safe) ---------------------------------------
// GA4 legge source/medium/campaign dagli UTM presenti in `page_location` del
// primo page_view della sessione. In una SPA con Consent Mode il primo
// page_view può essere ritardato fino all'accettazione dei cookie: per non
// perdere l'attribuzione salviamo gli UTM di atterraggio e li riallighiamo.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "gclid", "gbraid", "wbraid", "fbclid", "msclkid"];
const LANDING_UTM_KEY = "uvashop_landing_utm";

(function captureLandingUtm() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const kept = new URLSearchParams();
    UTM_KEYS.forEach((k) => { const v = sp.get(k); if (v) kept.set(k, v); });
    const qs = kept.toString();
    if (qs && !sessionStorage.getItem(LANDING_UTM_KEY)) {
      sessionStorage.setItem(LANDING_UTM_KEY, qs);
    }
  } catch { /* noop */ }
})();

function landingUtm() {
  try { return sessionStorage.getItem(LANDING_UTM_KEY) || ""; } catch { return ""; }
}

export function getSessionId() {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
}

const VID_KEY = "uvashop_vid";
export function getVisitorId() {
  try {
    let vid = localStorage.getItem(VID_KEY);
    if (!vid) {
      vid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VID_KEY, vid);
    }
    return vid;
  } catch {
    return "anon";
  }
}

// Evento registrazione (GA4 + Meta). success=true -> sign_up, altrimenti sign_up_error
export function trackSignUp(success, reason = "") {
  if (typeof window.gtag === "function" && localStorage.getItem("cookie_consent") === "accepted") {
    window.gtag("event", success ? "sign_up" : "sign_up_error", { method: "email", reason });
  }
  if (success && typeof window.fbq === "function") {
    try { window.fbq("track", "CompleteRegistration"); } catch { /* noop */ }
  }
}

function hasConsent() {
  try { return localStorage.getItem("cookie_consent") === "accepted"; } catch { return false; }
}

// Eventi tracciati anche nel nostro backend (dashboard nativa)
const NATIVE_EVENTS = new Set(["page_view", "view_item", "add_to_cart", "begin_checkout"]);

export function track(event, params = {}) {
  if (!hasConsent()) return;
  if (typeof window.gtag === "function") {
    const { _product_id, _product_name, _path, _referrer, ...gaParams } = params;
    window.gtag("event", event, gaParams);
  }
  if (typeof window.fbq === "function") {
    const FB_MAP = { page_view: "PageView", view_item: "ViewContent", add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout" };
    const fbEvent = FB_MAP[event];
    if (fbEvent) {
      const fp = {};
      if (params.value != null) { fp.value = Number(params.value) || 0; fp.currency = "EUR"; }
      if (params._product_id) { fp.content_ids = [params._product_id]; fp.content_type = "product"; }
      if (params._product_name) fp.content_name = params._product_name;
      window.fbq("track", fbEvent, fp);
    }
  }
  if (NATIVE_EVENTS.has(event)) {
    api.post("/analytics/track", {
      event,
      product_id: params._product_id || null,
      product_name: params._product_name || null,
      value: params.value ?? null,
      session_id: getSessionId(),
      path: params._path || window.location.pathname,
      referrer: params._referrer || null,
    }).catch(() => {});
  }
}

export function trackPageView(path) {
  // Garantisce che il page_location inviato a GA4 contenga gli UTM di
  // atterraggio (attribuzione di sessione a google / organic / campagna),
  // anche se l'URL corrente li ha già persi per la navigazione client-side.
  let pageLocation = window.location.href;
  const utm = landingUtm();
  if (utm && !/[?&]utm_/.test(pageLocation)) {
    pageLocation += (pageLocation.includes("?") ? "&" : "?") + utm;
  }
  track("page_view", {
    page_path: path,
    page_location: pageLocation,
    page_title: document.title,
    _path: path,
    _referrer: document.referrer || null,
  });
}

export function trackViewItem(wine) {
  if (!wine) return;
  track("view_item", {
    currency: "EUR",
    value: Number(wine.price) || 0,
    items: [{ item_id: wine.id, item_name: wine.name, price: Number(wine.price) || 0 }],
    _product_id: wine.id,
    _product_name: wine.name,
  });
}

export function trackAddToCart(wine, qty = 1) {
  if (!wine) return;
  track("add_to_cart", {
    currency: "EUR",
    value: (Number(wine.price) || 0) * qty,
    items: [{ item_id: wine.id, item_name: wine.name, price: Number(wine.price) || 0, quantity: qty }],
    _product_id: wine.id,
    _product_name: wine.name,
  });
}

export function trackBeginCheckout(items, value) {
  track("begin_checkout", {
    currency: "EUR",
    value: Number(value) || 0,
    items: (items || []).map((i) => ({ item_id: i.id, item_name: i.name, price: Number(i.price) || 0, quantity: i.quantity })),
  });
}

export function trackPurchase(transactionId, value) {
  // Solo GA4: i dati di acquisto della dashboard nativa provengono dagli ordini nel DB
  if (!hasConsent()) return;
  if (typeof window.gtag === "function") {
    window.gtag("event", "purchase", { transaction_id: transactionId, currency: "EUR", value: Number(value) || 0 });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "Purchase", { value: Number(value) || 0, currency: "EUR" });
  }
}
